import { all, call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { getSpotConfig } from '@host-panel/request/config';
import { getValueByPath, toCamelCaseObj } from '../../utils/objectUtils';

import { fetchMonetizationData, fetchMonetizationErrorMessage } from './monetizationActions';
import {
  FetchMonetizationDataPayload,
  FetchMonetizationDataResponse,
  MonetizationDataItem,
  MonetizationSummary
} from './monetizationTypes';
import {
  fetchMonetizationFailed,
  fetchMonetizationSuccess,
  setErrorMessage,
  setIsLoading
} from './monetizationSlice';
import { fetchMonetizationDataRequest } from './monetizationApi';

export const ERROR_MESSAGE_TEXT = 'tenant.monetization-analytics.error_message';
export const IS_ERROR_MESSAGE_ENABLED = 'tenant.monetization-analytics.error_message_enabled';

export function* fetchMonetizationDataSaga(action: PayloadAction<FetchMonetizationDataPayload>) {
  const { spotId, period, ...rest } = action.payload;

  yield put(setIsLoading(true));

  try {
    const response: FetchMonetizationDataResponse = yield call(fetchMonetizationDataRequest, {
      ...rest,
      spotId
    });

    const { summary, data } = response;
    const normalizedSummary = toCamelCaseObj(summary) as MonetizationSummary;
    const normalizedData = data.map(item => toCamelCaseObj(item)) as Array<MonetizationDataItem>;

    yield put(
      fetchMonetizationSuccess({
        summary: normalizedSummary,
        data: normalizedData,
        pageInfo: { period, ...rest }
      })
    );
  } catch (error) {
    console.error(error);

    yield put(fetchMonetizationFailed({ fromDay: rest.fromDay, toDay: rest.toDay }));
  }
}

export function* fetchMonetizationErrorMessageSaga(action: PayloadAction<string>) {
  const spotId = action.payload;

  try {
    const response = yield call(getSpotConfig, spotId, [
      IS_ERROR_MESSAGE_ENABLED,
      ERROR_MESSAGE_TEXT
    ]);

    const isErrorMessageEnabled = getValueByPath(response, IS_ERROR_MESSAGE_ENABLED)?.value;

    const errorMessage = isErrorMessageEnabled
      ? getValueByPath(response, ERROR_MESSAGE_TEXT)?.value
      : '';

    yield put(setErrorMessage(errorMessage));
  } catch (error) {
    console.error(error);
  }
}

export function* monetizationSagas() {
  yield all([
    takeLatest(fetchMonetizationData, fetchMonetizationDataSaga),
    takeLatest(fetchMonetizationErrorMessage, fetchMonetizationErrorMessageSaga)
  ]);
}
